import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import { useUrlState } from '../../hooks/useUrlState';
import ConfirmDialog from '../common/ConfirmDialog';
import OrderPdfUpload from '../orders/OrderPdfUpload';
import { useBrand } from '../../context/BrandContext';
import { useInventoryBrand } from '../../hooks/useInventoryBrand';
import { useSubsidiary } from '../../context/SubsidiaryContext';
import { excludeCompanyLevel } from '../../utils/brandFilters';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Stack,
  Paper,
  Tooltip,
  TableSortLabel,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as VoidIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  FilterList as FilterIcon,
  Paid as PaidIcon,
  PictureAsPdf as PdfIcon,
  RestoreFromTrash as RestoreIcon
} from '@mui/icons-material';
import {
  getRetailOrders,
  createRetailOrder,
  updateRetailOrder,
  deleteRetailOrder,
  voidWholesaleOrder,
  unvoidWholesaleOrder,
  unshipWholesaleOrder,
  uploadRetailOrdersCsv,
  parseRetailOrdersCSV,
  downloadOrderSourcePdf
} from '../../services/retailOrdersService';
import { getProductCosts, getWarehouses, getSkuBatches } from '../../services/inventoryService';
import api from '../../config/api';
import { getStatusColor } from '../common/statusColors';
import StatusMultiSelect from './StatusMultiSelect';
import { StatusBadge } from '../ds';
import { listOrderDrafts, deleteOrderDraft } from '../wholesale-orders/orderDraftsApi';
import { formatCountry } from '../../utils/countryFormat';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

// Fulfillment-status options for the filter (Rishabh 2026-07-30). Every real status in the data
// gets a checkbox so the count is honest and nothing is silently hidden — verified against the
// live retail_orders values: pending/processing/partially_shipped/in_transit/shipped/delivered/
// unknown/cancelled/voided. `delivered` is folded into `shipped` for the UI (one checkbox toggles
// the pair) — the caller adds `delivered` to the backend filter when `shipped` is chosen.
const FULFILLMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'partially_shipped', label: 'Partially shipped' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'voided', label: 'Voided' },
];

// Payment-status options — the real values in the data (verified live), replacing the old
// pending/partial/paid/failed list which had non-existent statuses and hid invoiced/unpaid/voided.
const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'voided', label: 'Voided' },
  { value: 'cancelled', label: 'Cancelled' },
];

function RetailOrders() {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const { selectedBrand, brands, selectBrand } = useInventoryBrand();
  const { subsidiaries } = useSubsidiary();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Data
  const [orders, setOrders] = useState([]);
  // Drafts (separate retail_order_drafts table) — shown pinned at the top of the
  // list, badged DRAFT, and excluded from every statistic (they're not in `orders`).
  const [drafts, setDrafts] = useState([]);
  const [statistics, setStatistics] = useState({
    total_orders: 0,
    pending_payment: 0,
    pending_fulfillment: 0,
    total_revenue: 0
  });
  // Unfiltered brand-wide totals for the KPI cards, so they match the Retail
  // Overview screen (which fetches stats with no fulfillment/status filter).
  // The list below stays scoped to the active filters.
  const [brandTotals, setBrandTotals] = useState({
    total_orders: 0,
    pending_payment: 0,
    pending_fulfillment: 0,
    total_revenue: 0
  });
  const [products, setProducts] = useState([]); // For SKU picker
  const [warehouses, setWarehouses] = useState([]); // For warehouse picker
  const [availableBatches, setAvailableBatches] = useState({}); // SKU -> batches mapping
  
  // Filters — URL-backed (useUrlState) so applying a filter, opening an order, and
  // hitting Back restores them. RetailOrders' edit action navigates to a DIFFERENT
  // component (/wholesale/new?edit=...), so the list fully unmounts; plain useState
  // reset every filter on return. (#7, Rishabh 2026-07-01 — platform-wide filter fix.)
  const [searchTerm, setSearchTerm] = useUrlState('q', '');
  const [dateFrom, setDateFrom] = useUrlState('from', '');
  const [dateTo, setDateTo] = useUrlState('to', '');
  const [sourceFilter, setSourceFilter] = useUrlState('source', '');
  const [paymentStatusFilter, setPaymentStatusFilter] = useUrlState('pay', '');
  // Comma-separated string of selected fulfillment statuses so it round-trips
  // through URL state cleanly. Default = all EXCEPT 'cancelled' + 'voided'
  // (active orders by default, matching the PO list page convention). Pattern
  // mirrors PurchaseOrders.js — multi-checkbox Select.
  //
  // Note 2026-05-22 (Rishabh): the 'Shipped' filter sends BOTH 'shipped' and
  // 'delivered' to the backend — they're treated as a single status in the UI.
  // EDI orders from THG land as 'delivered' in the DB; this filter + the
  // display-mapping below collapses the two. EDI ingestion untouched.
  const [fulfillmentStatusFilter, setFulfillmentStatusFilter] = useUrlState(
    'fulfil', 'pending,processing,partially_shipped,in_transit,shipped,delivered'
  );
  // Comma-separated shipping_country_code values. Default empty = all countries.
  const [countryFilter, setCountryFilter] = useUrlState('country', '');
  // "Ready to invoice" quick-view flag ('1' = on). Backend applies shipped + not-yet-invoiced
  // + not-voided (mirrors the old Ready-to-Invoice tab). Rishabh 2026-07-31.
  const [readyToInvoice, setReadyToInvoice] = useUrlState('ready', '');
  // Bumped by a quick-view preset AFTER it sets the filters, so the fetch fires on the next
  // render and loadData reads the just-committed filter state (not the stale closure).
  const [presetTick, setPresetTick] = useState(0);
  // Available country codes for the picker — fetched from the retail-orders
  // distinct endpoint on brand change. Shape: [{ country_code, n }].
  const [countryOptions, setCountryOptions] = useState([]);
  const [page, setPage] = useUrlState('p', 0, { parse: (v) => parseInt(v, 10) || 0 });
  const [rowsPerPage, setRowsPerPage] = useUrlState('pp', 25, { parse: (v) => parseInt(v, 10) || 25 });

  // Sorting
  const [orderBy, setOrderBy] = useUrlState('orderBy', 'event_date');
  const [order, setOrder] = useUrlState('order', 'desc');

  // Stash the list's filter query-string so the wholesale create/edit screen
  // (a SEPARATE component that fully unmounts this list) can send Back / Cancel /
  // Save back to the SAME filtered list, not the whole unfiltered list
  // (Rishabh 2026-07-02, platform-wide back fix — key mirrors PO/Transfers).
  useEffect(() => {
    try { sessionStorage.setItem('scm.retailListSearch', window.location.search || ''); } catch (_) { /* private mode */ }
  }, [searchTerm, dateFrom, dateTo, sourceFilter, paymentStatusFilter, fulfillmentStatusFilter, countryFilter, page, rowsPerPage, orderBy, order]);

  // Dialogs
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  // Void dialog — captures the audit-trail reason
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  // Restore (un-void) — moved here from the Pending Ship page (Rishabh 2026-09-24).
  // Reason is mandatory (audit trail); the backend re-reserves stock and 409s with the
  // shortage if it has since been used elsewhere.
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreReason, setRestoreReason] = useState('');
  const [restoring, setRestoring] = useState(false);
  const handleRestoreVoided = (order) => { setError(null); setRestoreTarget(order); setRestoreReason(''); };
  const submitRestore = async () => {
    if (!restoreReason.trim() || !selectedBrand?.id || !restoreTarget) return;
    try {
      setRestoring(true);
      await unvoidWholesaleOrder(selectedBrand.id, restoreTarget.order_id, restoreReason.trim());
      setSuccess(`Restored ${restoreTarget.order_name || restoreTarget.order_id}: the order is pending again.`);
      setRestoreTarget(null);
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to restore order'));
    } finally { setRestoring(false); }
  };
  const [voiding, setVoiding] = useState(false);

  const openVoid = (order) => {
    setVoidTarget(order);
    setVoidReason('');
    setError(null); setVoidOpen(true);
  };
  const submitVoid = async () => {
    if (!voidReason.trim() || !selectedBrand?.id || !voidTarget) {
      setError('A reason is required (audit trail).');
      return;
    }
    try {
      setVoiding(true);
      const orderLabel = (voidTarget.order_id || '').replace(/^CIN7-/, '');
      const isShipped = ['shipped', 'partially_shipped'].includes(voidTarget.fulfillment_status);
      if (isShipped) {
        // Walk back every shipment, then void. Multi-shipment orders need
        // multiple unship passes — each call reverses the LATEST remaining
        // 'shipped' fulfillment and either flips the order to 'pending' (no
        // more shipped fulfillments) or 'partially_shipped' (older partials
        // remain). Loop until 'pending', then chain the void. Cap at 20
        // iterations as a safety net — real orders won't exceed a handful.
        const reasonTrim = voidReason.trim();
        const MAX_UNSHIPS = 20;
        let r = null;
        let unshipsDone = 0;
        let stopReason = null;

        while (unshipsDone < MAX_UNSHIPS) {
          try {
            r = await unshipWholesaleOrder(selectedBrand.id, voidTarget.order_id, reasonTrim);
          } catch (e) {
            // Backend returns 409 when no shipped fulfillment remains. Treat
            // that as "we've reversed everything we can" — bail to status
            // check below; the void chain will decide whether to fire.
            const body = e?.response?.data || {};
            stopReason = `${body.error || e?.message || 'unship failed'}${body.detail ? ` — ${body.detail}` : ''}`;
            break;
          }
          unshipsDone += 1;
          const hasPartials = (r?.partial_items?.length || 0) > 0
                           || (r?.missing_batches?.length || 0) > 0;
          if (hasPartials || r?.warning) {
            // Partial restore — surface to operator; do NOT auto-void since
            // missing-batch state needs human review.
            stopReason = r?.warning || 'partial restore — missing batches need manual review';
            break;
          }
          if (r?.new_status === 'pending') break;
          if (r?.new_status !== 'partially_shipped') {
            stopReason = `unexpected status '${r?.new_status}' after unship`;
            break;
          }
          // else: still partially_shipped — loop and reverse the next one.
        }

        const fullyUnshipped = r?.new_status === 'pending' && !stopReason;
        if (fullyUnshipped) {
          try {
            await voidWholesaleOrder(selectedBrand.id, voidTarget.order_id, reasonTrim);
            setSuccess(`Voided ${orderLabel} — ${unshipsDone} shipment${unshipsDone === 1 ? '' : 's'} reversed, inventory restored, order voided.`);
          } catch (voidErr) {
            const body = voidErr?.response?.data || {};
            const detail = body.detail ? ` — ${body.detail}` : '';
            setError(`Shipments cancelled but couldn't complete void (${body.error || voidErr.message}${detail}). Order is in pending status — click Void again to finish.`);
          }
        } else if (unshipsDone >= MAX_UNSHIPS) {
          setError(`${orderLabel}: more than ${MAX_UNSHIPS} prior shipments — too many to auto-cancel. Cancel manually.`);
        } else {
          // Stopped before pending — surface why. Operator can re-click to
          // continue or investigate the warning.
          setError(`${orderLabel}: ${unshipsDone} shipment${unshipsDone === 1 ? '' : 's'} reversed but stopped before void${stopReason ? ` — ${stopReason}` : ''}.`);
        }
      } else {
        await voidWholesaleOrder(selectedBrand.id, voidTarget.order_id, voidReason.trim());
        setSuccess(`Voided ${orderLabel} — reason logged.`);
      }
      setVoidOpen(false);
      await loadData();
    } catch (e) {
      // Show the backend's `detail` (CH error / JS exception message) alongside
      // the top-level error so "Failed to revert shipment" comes with the
      // actual cause instead of just the headline.
      const body = e?.response?.data || {};
      const headline = body.error || e?.message || 'Action failed';
      const detail = body.detail ? ` — ${body.detail}` : '';
      const hint = body.hint ? ` (${body.hint})` : '';
      setError(`${headline}${detail}${hint}`);
    } finally { setVoiding(false); }
  };
  
  // Form state
  const [orderForm, setOrderForm] = useState({
    order_name: '',
    event_date: new Date().toISOString().split('T')[0],
    source: 'Manual',
    warehouse_code: '', // Source warehouse for batch selection
    subsidiary_id: 0, // Invoicing subsidiary (0 = Unmapped)
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    products_info: [{ product_sku: '', gtin: '', product_name: '', quantity: 1, unit_price: 0, cost_per_unit: 0, batch_id: '' }],
    currency: 'GBP',
    payment_terms: '',
    payment_method: '',
    payment_status: 'pending',
    po_number: '',
    invoice_number: '',
    sales_rep: '',
    notes: ''
  });
  // The warehouse on screen now (DT-989): a batch read for any other warehouse is dropped when it answers.
  const warehouseRef = React.useRef(orderForm.warehouse_code);
  warehouseRef.current = orderForm.warehouse_code;

  const [csvFile, setCsvFile] = useState(null);

  useEffect(() => {
    if (selectedBrand?.id) {
      loadData();
    }
  }, [selectedBrand?.id]);

  // Populate the country dropdown — fetched once per brand change.
  useEffect(() => {
    if (!selectedBrand?.id) { setCountryOptions([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
        const r = await api.get(`${API_BASE}/api/cogs/brands/${selectedBrand.id}/retail-orders/countries`);
        if (!cancelled) setCountryOptions(r.data?.data || []);
      } catch (e) {
        if (!cancelled) setCountryOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBrand?.id]);

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      selectBrand(brand);
    }
  };

  const loadData = async () => {
    if (!selectedBrand?.id) {
      setError('Please select a brand first');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Build filter params
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (sourceFilter) params.source = sourceFilter;
      if (paymentStatusFilter) params.payment_status = paymentStatusFilter;
      if (fulfillmentStatusFilter) params.fulfillment_status = fulfillmentStatusFilter;
      if (countryFilter) params.shipping_country_code = countryFilter;
      if (readyToInvoice === '1') params.ready_to_invoice = true;
      // To dispatch preset hides orders closed with "rest won't ship".
      if (fulfillmentStatusFilter === 'pending,processing,partially_shipped') params.exclude_closed = true;

      const [ordersRes, brandTotalsRes, productsRes, warehousesRes, draftsRes] = await Promise.all([
        getRetailOrders(selectedBrand.id, params),
        // Unfiltered brand totals for the KPI cards (match the Retail Overview).
        // limit:1 — we only need the statistics block, not the rows. Best-effort:
        // a failure here must not break the page (cards fall back to filtered).
        getRetailOrders(selectedBrand.id, { limit: 1 }).catch(() => null),
        getProductCosts(selectedBrand.id, { limit: 1000 }),
        getWarehouses({ is_active: true }),
        // Drafts are best-effort — a failure here must never break the orders page.
        listOrderDrafts(selectedBrand.id).catch(() => ({ drafts: [] }))
      ]);

      setOrders(ordersRes?.data || []);
      setStatistics(ordersRes?.statistics || {
        total_orders: 0,
        pending_payment: 0,
        pending_fulfillment: 0,
        total_revenue: 0
      });
      setBrandTotals((brandTotalsRes || ordersRes)?.statistics || {
        total_orders: 0,
        pending_payment: 0,
        pending_fulfillment: 0,
        total_revenue: 0
      });
      setProducts(productsRes?.data || []);
      setWarehouses(warehousesRes?.data || []);
      setDrafts(draftsRes?.drafts || []);
    } catch (err) {
      console.error('Failed to load retail orders:', err);
      setError('Failed to load retail orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fire the fetch once a quick-view preset has committed its filter state (presetTick bump).
  // Skip the mount run so it doesn't double-load alongside the brand-load effect.
  const didMountPreset = React.useRef(false);
  useEffect(() => {
    if (!didMountPreset.current) { didMountPreset.current = true; return; }
    if (selectedBrand?.id) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetTick]);

  // Quick views replace the old Pending-Ship / Ready-to-Invoice tabs (Rishabh 2026-07-31): set the
  // exact filters those tabs used, clear the rest, then load. Pending ship = fulfillment awaiting
  // shipment; Ready to invoice = shipped + not-yet-invoiced (the backend applies the qb_invoice_id
  // condition via ready_to_invoice).
  // "To dispatch" (Rishabh 2026-09-24, replaces "Pending ship"): orders whose stock hasn't
  // left yet — pending/processing, plus part-shipped portal orders not closed with "rest won't
  // ship". Dispatched (in_transit) orders move on to Ready to invoice.
  const TO_DISPATCH = 'pending,processing,partially_shipped';
  const applyPendingShip = () => {
    setPaymentStatusFilter('');
    setFulfillmentStatusFilter(TO_DISPATCH);
    setCountryFilter('');
    setReadyToInvoice('');
    setPage(0);
    setPresetTick((t) => t + 1);
  };
  const applyReadyToInvoice = () => {
    // Clear the visible Fulfillment filter — the backend `ready_to_invoice` flag defines the full
    // set (shipped/pending/in_transit + procuros partials, uninvoiced, not voided). Setting a
    // narrower Fulfillment here would AND with it and drop pending/in_transit/procuros orders.
    setPaymentStatusFilter('');
    setFulfillmentStatusFilter('');
    setCountryFilter('');
    setReadyToInvoice('1');
    setPage(0);
    setPresetTick((t) => t + 1);
  };
  const clearQuickView = () => {
    // Reset to the page's DEFAULT view (active orders), not '' — an empty fulfillment filter would
    // show ALL statuses incl. cancelled/voided, a broader set than the user's normal list.
    setPaymentStatusFilter('');
    setFulfillmentStatusFilter('pending,processing,partially_shipped,in_transit,shipped,delivered');
    setCountryFilter('');
    setReadyToInvoice('');
    setPage(0);
    setPresetTick((t) => t + 1);
  };

  // Discard a draft (tombstone). Optimistically drop it from the list, then reload.
  const handleDeleteDraft = async (draftIdToDelete) => {
    if (!selectedBrand?.id) return;
    try {
      setDrafts(prev => prev.filter(d => d.draft_id !== draftIdToDelete));
      await deleteOrderDraft(selectedBrand.id, draftIdToDelete);
      setSuccess('Draft discarded.');
    } catch (e) {
      setError(apiErrorMessage(e));
      loadData();
    }
  };

  const handleOpenOrderDialog = (order = null) => {
    setAvailableBatches({}); // Reset batch selections
    if (order) {
      // Parse products_info if it's a string
      let productsInfo = order.products_info;
      if (typeof productsInfo === 'string') {
        try {
          productsInfo = JSON.parse(productsInfo);
        } catch (e) {
          productsInfo = [{ product_sku: '', gtin: '', product_name: '', quantity: 1, unit_price: 0, cost_per_unit: 0, batch_id: '' }];
        }
      }
      // Ensure batch_id field exists on all products
      productsInfo = productsInfo.map(p => ({ ...p, batch_id: p.batch_id || '' }));

      setEditingOrder(order);
      setOrderForm({
        order_name: order.order_name || '',
        event_date: order.event_date || new Date().toISOString().split('T')[0],
        source: order.source || 'Manual',
        warehouse_code: order.warehouse_code || '',
        subsidiary_id: Number(order.subsidiary_id) || 0,
        customer_name: order.customer_name || '',
        customer_email: order.customer_email || '',
        customer_phone: order.customer_phone || '',
        products_info: productsInfo || [{ product_sku: '', gtin: '', product_name: '', quantity: 1, unit_price: 0, cost_per_unit: 0, batch_id: '' }],
        currency: order.currency || 'GBP',
        payment_terms: order.payment_terms || '',
        payment_method: order.payment_method || '',
        payment_status: order.payment_status || 'pending',
        po_number: order.po_number || '',
        invoice_number: order.invoice_number || '',
        sales_rep: order.sales_rep || '',
        notes: order.notes || '',
        source_file: order.source_file || ''
      });
    } else {
      setEditingOrder(null);
      setOrderForm({
        order_name: '',
        event_date: new Date().toISOString().split('T')[0],
        source: 'Manual',
        warehouse_code: '',
        subsidiary_id: 0,
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        products_info: [{ product_sku: '', gtin: '', product_name: '', quantity: 1, unit_price: 0, cost_per_unit: 0, batch_id: '' }],
        currency: 'GBP',
        payment_terms: '',
        payment_method: '',
        payment_status: 'pending',
        po_number: '',
        invoice_number: '',
        sales_rep: '',
        notes: '',
        source_file: ''
      });
    }
    setError(null); setOrderDialogOpen(true);
  };

  // Hydrate the manual order form from a parsed PDF prefill payload. Low-confidence
  // SKUs come back as '' (highlighted in the upload preview) so the operator picks
  // them in the line rows. order_name falls back to PO# to satisfy the required field.
  const applyPdfPrefill = (prefill) => {
    const o = prefill.order || {};
    setOrderForm((prev) => ({
      ...prev,
      order_name: o.order_name || o.po_number || prev.order_name,
      customer_name: o.customer_name || prev.customer_name,
      customer_email: o.customer_email || prev.customer_email,
      currency: o.currency || prev.currency,
      payment_terms: o.payment_terms || prev.payment_terms,
      po_number: o.po_number || prev.po_number,
      event_date: o.event_date || prev.event_date,
      notes: o.order_notes || prev.notes,
      source_file: prefill.source_file || prev.source_file,
      products_info: (prefill.line_items || []).length
        ? prefill.line_items.map((li) => ({
            product_sku: li.product_sku || '',
            gtin: li.gtin || '',
            product_name: li.product_name || li.raw_text || '',
            quantity: li.quantity || 1,
            unit_price: li.unit_price || 0,
            cost_per_unit: 0,
            batch_id: ''
          }))
        : prev.products_info
    }));
  };

  const handleAddProduct = () => {
    setOrderForm({
      ...orderForm,
      products_info: [...orderForm.products_info, { product_sku: '', gtin: '', product_name: '', quantity: 1, unit_price: 0, cost_per_unit: 0, batch_id: '' }]
    });
  };

  const handleRemoveProduct = (index) => {
    const newProducts = orderForm.products_info.filter((_, i) => i !== index);
    setOrderForm({ ...orderForm, products_info: newProducts });
  };

  const handleUpdateProduct = async (index, field, value) => {
    const newProducts = [...orderForm.products_info];
    newProducts[index] = { ...newProducts[index], [field]: value };

    // Auto-populate product details when SKU is selected
    if (field === 'product_sku') {
      const product = products.find(p => p.sku === value);
      if (product) {
        newProducts[index].product_name = product.product_name || '';
        newProducts[index].gtin = product.gtin || '';
        newProducts[index].cost_per_unit = parseFloat(product.cogs || product.unit_cost || 0);
      }
      // Clear batch selection when SKU changes
      newProducts[index].batch_id = '';
      // Fetch available batches for this SKU if warehouse is selected
      if (value && orderForm.warehouse_code) {
        fetchBatchesForSku(value, orderForm.warehouse_code);
      }
    }

    setOrderForm({ ...orderForm, products_info: newProducts });
  };

  // Fetch available batches for a SKU in the selected warehouse
  const fetchBatchesForSku = async (sku, warehouseCode) => {
    if (!sku || !warehouseCode) return;
    try {
      const response = await getSkuBatches(sku, { warehouse_code: warehouseCode, sort: 'lefo' });
      if (warehouseRef.current !== warehouseCode) return; // DT-989: answer for a warehouse no longer selected
      if (response?.data?.batches) {
        // Filter to only show non-expired batches with available quantity.
        // Exclude the synthetic "Other stock" pseudo-batch (id 0): it has no real
        // backing batch row to allocate from, and selecting it silently discards the
        // choice at submit (0 is falsy, so batch_ids falls back to auto_select). The
        // "Auto (LEFO)" option already covers this remainder stock correctly.
        const available = response.data.batches.filter(
          b => !b.is_expired && b.current_quantity > 0 && b.id !== 0 && b.batch_number !== 'Other stock'
        );
        setAvailableBatches(prev => ({ ...prev, [sku]: available }));
      }
    } catch (err) {
      console.error('Failed to fetch batches for SKU:', sku, err);
    }
  };

  // Handle warehouse change - fetch batches for all SKUs in the order
  const handleWarehouseChange = async (warehouseCode) => {
    warehouseRef.current = warehouseCode; // before any await, so reads for the previous warehouse are dropped
    setOrderForm({ ...orderForm, warehouse_code: warehouseCode });
    // Clear existing batch selections and fetch new batches
    const newProducts = orderForm.products_info.map(p => ({ ...p, batch_id: '' }));
    setOrderForm({ ...orderForm, warehouse_code: warehouseCode, products_info: newProducts });
    setAvailableBatches({});
    // Fetch batches for all SKUs that have been selected
    for (const product of orderForm.products_info) {
      if (warehouseRef.current !== warehouseCode) break; // changed again: the newer call fetches its own
      if (product.product_sku) {
        await fetchBatchesForSku(product.product_sku, warehouseCode);
      }
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'GBP': '£',
      'USD': '$',
      'EUR': '€',
      'AUD': 'A$',
      'CAD': 'C$',
      'INR': '₹'
    };
    return symbols[currency] || currency;
  };

  const calculateTotal = () => {
    return orderForm.products_info.reduce((total, p) => {
      const qty = parseFloat(p.quantity) || 0;
      const price = parseFloat(p.unit_price) || 0;
      return total + (qty * price);
    }, 0);
  };

  const handleSaveOrder = async () => {
    if (!selectedBrand?.id) {
      setError('Please select a brand first');
      return;
    }

    // Validation
    if (!orderForm.order_name) {
      setError('Order name is required');
      return;
    }
    if (!orderForm.customer_name) {
      setError('Customer name is required');
      return;
    }
    if (orderForm.products_info.length === 0 || !orderForm.products_info[0].product_sku) {
      setError('At least one product is required');
      return;
    }

    try {
      setError(null);
      // Convert batch_id to batch_ids array format expected by backend
      const productsWithBatchIds = orderForm.products_info.map(p => ({
        ...p,
        batch_ids: p.batch_id ? [parseInt(p.batch_id, 10)] : undefined
      }));

      const orderData = {
        ...orderForm,
        products_info: productsWithBatchIds,
        total_amount: calculateTotal(),
        // Use manual batch selection if any batches are selected
        auto_select: !productsWithBatchIds.some(p => p.batch_ids && p.batch_ids.length > 0)
      };

      if (editingOrder) {
        await updateRetailOrder(selectedBrand.id, editingOrder.order_id, orderData);
        setSuccess('Order updated successfully');
      } else {
        await createRetailOrder(selectedBrand.id, orderData);
        setSuccess('Order created successfully');
      }

      setOrderDialogOpen(false);
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save order'));
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!await confirm({ title: 'Delete Order', message: 'Are you sure you want to delete this order?', confirmText: 'Delete', severity: 'danger' })) return;
    
    try {
      await deleteRetailOrder(selectedBrand.id, orderId);
      setSuccess('Order deleted successfully');
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to delete order'));
    }
  };

  const handleMarkPaid = async (order) => {
    if (!await confirm({ title: 'Mark as Paid', message: `Mark order ${order.order_name || order.order_id} as paid?`, confirmText: 'Mark Paid' })) return;
    try {
      await api.post(`${process.env.REACT_APP_API_URL || 'https://api.superchargeme.com'}/api/cogs/brands/${selectedBrand.id}/retail-orders/${encodeURIComponent(order.order_id)}/mark-paid`);
      setSuccess(`Order ${order.order_name || order.order_id} marked as paid`);
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to mark order as paid'));
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    if (!selectedBrand?.id) {
      setError('Please select a brand first');
      return;
    }

    try {
      setError(null);
      setLoading(true);
      
      // Read CSV file
      const fileText = await csvFile.text();
      const csvData = parseRetailOrdersCSV(fileText);
      
      if (csvData.length === 0) {
        setError('CSV file contains no valid data rows');
        return;
      }
      
      // Upload CSV data
      const result = await uploadRetailOrdersCsv(selectedBrand.id, csvData);
      
      if (result.success) {
        setSuccess(
          `Upload complete: ${result.results.success} orders imported successfully` +
          (result.results.failed > 0 ? `, ${result.results.failed} failed` : '')
        );
        
        if (result.results.errors && result.results.errors.length > 0) {
          console.error('CSV upload errors:', result.results.errors);
        }
        
        setCsvDialogOpen(false);
        setCsvFile(null);
        loadData();
      }
    } catch (err) {
      console.error('CSV upload error:', err);
      setError(apiErrorMessage(err, 'Failed to upload CSV file'));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aVal, bVal;
    
    if (orderBy === 'total_amount') {
      aVal = parseFloat(a.total_amount) || 0;
      bVal = parseFloat(b.total_amount) || 0;
    } else if (orderBy === 'event_date') {
      aVal = new Date(a.event_date || 0).getTime();
      bVal = new Date(b.event_date || 0).getTime();
    } else {
      aVal = a[orderBy] || '';
      bVal = b[orderBy] || '';
    }
    
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
  };

  const filteredOrders = orders.filter(order => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return true;
    // Strip the CIN7- prefix from order_id when searching — operators paste in
    // either the bare numeric or the prefixed form. Also normalize case.
    const orderIdLc = String(order.order_id || '').toLowerCase();
    const orderIdNoPrefix = orderIdLc.replace(/^cin7-/, '');
    return (
      orderIdLc.includes(q) ||
      orderIdNoPrefix.includes(q) ||
      String(order.order_name || '').toLowerCase().includes(q) ||
      String(order.customer_name || '').toLowerCase().includes(q) ||
      String(order.po_number || '').toLowerCase().includes(q)
    );
  }).sort(getComparator(order, orderBy));

  // Derived stats — recomputed from filteredOrders so the cards always reflect
  // the currently visible set (incl. the client-side searchTerm). Falls back
  // to the backend `statistics` block for total_revenue since per-row currency
  // conversion would require re-doing the FX work the backend already did.
  const displayStats = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const pendingPayment = filteredOrders.filter(o => o.payment_status === 'pending').length;
    const pendingFulfillment = filteredOrders.filter(o => o.fulfillment_status === 'pending').length;
    // Revenue: when no client-side search is applied, trust backend's currency-
    // converted total; otherwise sum the visible rows in their native currency
    // (less accurate but reflects what the operator sees).
    const totalRevenue = searchTerm
      ? filteredOrders.reduce((sum, o) => sum + (Number(o.order_revenue) || 0), 0)
      : (statistics.total_revenue || 0);
    return {
      total_orders: totalOrders,
      pending_payment: pendingPayment,
      pending_fulfillment: pendingFulfillment,
      total_revenue: totalRevenue,
      revenue_is_converted: !searchTerm,
    };
  }, [filteredOrders, searchTerm, statistics.total_revenue]);

  // Use the shared status palette (frontend/src/components/common/statusColors.js)
  // so the same status renders the same color across PO, transfer, wholesale, etc.

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="center">
            <ReceiptIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Retail Orders
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage B2B, wholesale, and manual retail orders
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Select Brand</InputLabel>
              <Select
                value={selectedBrand?.id || ''}
                onChange={handleBrandChange}
                label="Select Brand"
              >
                {excludeCompanyLevel(brands).map(brand => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => { setError(null); setCsvDialogOpen(true); }}
                disabled={!selectedBrand?.id}
              >
                CSV Upload
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/wholesale/new?brand=${selectedBrand?.id || ''}`)}
                disabled={!selectedBrand?.id}
                title="Open the wholesale order form"
              >
                Add New Order
              </Button>
              <Tooltip title="Refresh data">
                <IconButton onClick={loadData} color="primary" disabled={!selectedBrand?.id}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Brand Selection Alert */}
      {!selectedBrand && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select a brand above to view retail orders
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Summary Cards — headline figure is the brand-wide total (matches the
          Retail Overview screen); the caption shows the subset in the current
          filter/search below. */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">Total Orders</Typography>
              <Typography variant="h4" fontWeight="bold">{brandTotals.total_orders}</Typography>
              <Typography variant="caption" color="textSecondary">{displayStats.total_orders} in current filter</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">Pending Payment</Typography>
              <Typography variant="h4" fontWeight="bold">
                {brandTotals.pending_payment}
              </Typography>
              <Typography variant="caption" color="textSecondary">{displayStats.pending_payment} in current filter</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">Pending Fulfillment</Typography>
              <Typography variant="h4" fontWeight="bold">
                {brandTotals.pending_fulfillment}
              </Typography>
              <Typography variant="caption" color="textSecondary">{displayStats.pending_fulfillment} in current filter</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">Total Revenue (USD)</Typography>
              <Typography variant="h4" fontWeight="bold">
                ${Number(brandTotals.total_revenue || 0).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                ${Number(displayStats.total_revenue || 0).toFixed(2)} in current filter{displayStats.revenue_is_converted ? '' : ' (native)'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Quick views — one-click presets replacing the old Pending-Ship / Ready-to-Invoice
              tabs (Rishabh 2026-07-31). Each sets the exact filters those tabs used, then loads. */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Quick views:</Typography>
            <Chip
              label="To dispatch"
              size="small"
              color={fulfillmentStatusFilter === TO_DISPATCH && readyToInvoice !== '1' ? 'primary' : 'default'}
              variant={fulfillmentStatusFilter === TO_DISPATCH && readyToInvoice !== '1' ? 'filled' : 'outlined'}
              onClick={applyPendingShip}
            />
            <Chip
              label="Ready to invoice"
              size="small"
              color={readyToInvoice === '1' ? 'primary' : 'default'}
              variant={readyToInvoice === '1' ? 'filled' : 'outlined'}
              onClick={applyReadyToInvoice}
            />
            <Chip label="Clear" size="small" variant="outlined" onClick={clearQuickView} />
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="From Date"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: '2000-01-01' }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="To Date"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: '2000-01-01' }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <StatusMultiSelect
                label="Payment Status"
                options={PAYMENT_STATUS_OPTIONS}
                value={paymentStatusFilter ? paymentStatusFilter.split(',').filter(Boolean) : []}
                onChange={(next) => { setPaymentStatusFilter(next.join(',')); setReadyToInvoice(''); }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <StatusMultiSelect
                label="Fulfillment Status"
                options={FULFILLMENT_STATUS_OPTIONS}
                // Visible selection = the stored filter minus `delivered` (folded into `shipped`).
                value={(fulfillmentStatusFilter ? fulfillmentStatusFilter.split(',').filter(Boolean) : [])
                  .filter((s) => s !== 'delivered')}
                onChange={(next) => {
                  // `shipped` implies `delivered` in the backend filter (EDI 'delivered' is a kind
                  // of shipped for operators — Rishabh 2026-05-22). Keep that pairing here.
                  const final = next.includes('shipped')
                    ? Array.from(new Set([...next, 'delivered']))
                    : next;
                  setFulfillmentStatusFilter(final.join(','));
                  setReadyToInvoice(''); // a manual status edit exits the Ready-to-invoice preset
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              {/* Country uses the same StatusMultiSelect as the other two filters (Rishabh
                  2026-07-30: all three consistent). This also removes the old onClose={loadData}
                  that re-fetched on every dropdown close — the "page refreshes when I click
                  outside the country filter" bug. Country now applies via the Apply button like
                  the others. Options come from countryOptions (dynamic, backend-normalised codes). */}
              <StatusMultiSelect
                label="Country"
                options={countryOptions.map((o) => ({
                  value: o.country_code,
                  label: o.country_label || formatCountry(o.country_code) || o.country_code,
                }))}
                value={countryFilter ? countryFilter.split(',').filter(Boolean) : []}
                onChange={(next) => { setCountryFilter(next.join(',')); setReadyToInvoice(''); }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={loadData}
                fullWidth
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'order_name' || orderBy === 'order_id'}
                      direction={(orderBy === 'order_name' || orderBy === 'order_id') ? order : 'asc'}
                      onClick={() => handleRequestSort('order_name')}
                    >
                      Order #
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'event_date'}
                      direction={orderBy === 'event_date' ? order : 'asc'}
                      onClick={() => handleRequestSort('event_date')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'customer_name'}
                      direction={orderBy === 'customer_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('customer_name')}
                    >
                      Customer
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'po_number'}
                      direction={orderBy === 'po_number' ? order : 'asc'}
                      onClick={() => handleRequestSort('po_number')}
                    >
                      Customer PO
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'total_amount'}
                      direction={orderBy === 'total_amount' ? order : 'asc'}
                      onClick={() => handleRequestSort('total_amount')}
                    >
                      Total
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'payment_status'}
                      direction={orderBy === 'payment_status' ? order : 'asc'}
                      onClick={() => handleRequestSort('payment_status')}
                    >
                      Payment
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'fulfillment_status'}
                      direction={orderBy === 'fulfillment_status' ? order : 'asc'}
                      onClick={() => handleRequestSort('fulfillment_status')}
                    >
                      Fulfillment
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'source'}
                      direction={orderBy === 'source' ? order : 'asc'}
                      onClick={() => handleRequestSort('source')}
                    >
                      Source
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Drafts pinned at the top (first page only). Separate table from
                    retail_orders, so excluded from every statistic. Each row links
                    back into the order form to finalize or can be discarded. */}
                {page === 0 && drafts
                  .filter(d => {
                    if (!searchTerm) return true;
                    const q = searchTerm.toLowerCase();
                    return String(d.customer_name || '').toLowerCase().includes(q)
                      || String(d.order_name || '').toLowerCase().includes(q);
                  })
                  .map((draft) => (
                    <TableRow key={`draft-${draft.draft_id}`} hover sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip label="DRAFT" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'grey.300', color: 'text.secondary' }} />
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{draft.order_name || '(no PO yet)'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{draft.event_date ? new Date(draft.event_date).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{draft.customer_name || '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{draft.po_number || '—'}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                          {getCurrencySymbol(draft.currency || 'GBP')}{parseFloat(draft.est_total || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>—</TableCell>
                      <TableCell><Chip label="Draft" size="small" sx={{ height: 18, fontSize: 10, bgcolor: 'grey.200', color: 'text.secondary' }} /></TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{draft.origin === 'pdf' ? 'PDF draft' : 'Draft'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" title="Open & finalize this draft"
                          onClick={() => navigate(`/wholesale/new?draft=${encodeURIComponent(draft.draft_id)}&brand=${selectedBrand.id}`)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" title="Discard this draft"
                          onClick={() => handleDeleteDraft(draft.draft_id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredOrders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((order) => (
                    <TableRow key={order.order_id} hover>
                      <TableCell>
                        {/* Combined Order # — order_name (CIN7-style native,
                            e.g. ISER2600-30) is the primary label; order_id
                            stripped of CIN7- prefix becomes the monospace
                            sublabel for traceability. */}
                        {(() => {
                          const oidStripped = (order.order_id || '').replace(/^CIN7-/, '');
                          const oname = order.order_name || '';
                          const showBoth = oname && oname !== oidStripped;
                          // Order # opens the same view the Edit pencil does:
                          // wholesale rows → wholesale form, DTC/Manual → order dialog.
                          // Voided orders aren't navigable (pencil is disabled), so plain text.
                          const openOrder = (e) => {
                            e.stopPropagation();
                            if (['retailer','distributor'].includes(order.customer_type) && selectedBrand?.id) {
                              navigate(`/wholesale/new?edit=${encodeURIComponent(order.order_id)}&brand=${selectedBrand.id}`);
                            } else {
                              handleOpenOrderDialog(order);
                            }
                          };
                          return (
                            <>
                              {order.is_voided === 1 ? (
                                <Typography fontWeight="medium">{showBoth ? oname : oidStripped}</Typography>
                              ) : (
                                <Box
                                  component="a"
                                  title="Open order"
                                  onClick={openOrder}
                                  sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                  {showBoth ? oname : oidStripped}
                                </Box>
                              )}
                              {showBoth && (
                                <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'text.disabled', fontFamily: 'monospace', lineHeight: 1 }}>
                                  {oidStripped}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {order.event_date ? new Date(order.event_date).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>{order.customer_name || '—'}</TableCell>
                      <TableCell>{order.po_number || '—'}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {getCurrencySymbol(order.currency || 'GBP')}{parseFloat(order.total_amount || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {String(order.payment_status || '').toLowerCase() === 'invoiced' ? (
                          // "Invoiced" only means an invoice was raised, not a confirmed
                          // payment outcome — de-emphasise it to small muted text so it no
                          // longer reads like the green "Paid" badge. Badges stay for Paid
                          // and the awaiting states (Rishabh 2026-07-21).
                          <Typography variant="caption" color="text.secondary">Invoiced</Typography>
                        ) : (
                          <StatusBadge status={order.payment_status || 'pending'} />
                        )}
                      </TableCell>
                      <TableCell>
                        {order.is_voided === 1 ? (
                          <StatusBadge status="voided" />
                        ) : (
                          // 2026-05-22 (Rishabh): collapse 'delivered' → 'shipped' visually.
                          // EDI/THG orders land as 'delivered' in the DB and we keep that
                          // (so the historical signal isn't lost), but operators want a
                          // single 'Shipped' label for both. Display-only mapping; the
                          // underlying retail_orders.fulfillment_status is unchanged.
                          <StatusBadge status={
                            order.fulfillment_status === 'delivered'
                              ? 'shipped'
                              : (order.fulfillment_status || 'pending')
                          } />
                        )}
                      </TableCell>
                      <TableCell>
                        {order.source || 'Manual'}
                        {typeof order.source_file === 'string' && order.source_file.startsWith('order-pdfs/') && (
                          <Tooltip title="View the source PDF this order was created from">
                            <IconButton
                              size="small"
                              sx={{ ml: 0.5 }}
                              onClick={() => downloadOrderSourcePdf(selectedBrand.id, order.order_id)}
                            >
                              <PdfIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          title={['retailer','distributor'].includes(order.customer_type) ? 'Edit in wholesale form' : 'Edit'}
                          disabled={order.is_voided === 1}
                          onClick={() => {
                            // Any wholesale-customer row (customer_type retailer
                            // or distributor) — including CIN7-imported orders —
                            // routes to the full wholesale form. DTC / Manual /
                            // Shopify retail orders stay on the legacy dialog
                            // since they lack the CWE / tier context.
                            if (['retailer','distributor'].includes(order.customer_type) && selectedBrand?.id) {
                              navigate(`/wholesale/new?edit=${encodeURIComponent(order.order_id)}&brand=${selectedBrand.id}`);
                            } else {
                              handleOpenOrderDialog(order);
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {/* Void — soft-cancel with required reason for audit
                            trail. Hidden once already voided. Only enabled for
                            wholesale rows; DTC orders use the delete path. */}
                        {order.is_voided !== 1 && ['retailer','distributor'].includes(order.customer_type)
                          && ['wholesale_native', 'cin7_wholesale', 'cin7'].includes(order.source) && (
                          <IconButton
                            size="small"
                            color="warning"
                            title="Void this order (audit trail)"
                            onClick={() => openVoid(order)}
                          >
                            <VoidIcon fontSize="small" />
                          </IconButton>
                        )}
                        {order.is_voided === 1 && ['wholesale_native', 'cin7_wholesale', 'cin7'].includes(order.source) && (
                          <IconButton
                            size="small"
                            color="primary"
                            title="Restore this voided order (back to Pending, stock re-reserved)"
                            onClick={() => handleRestoreVoided(order)}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        )}
                        {order.is_voided !== 1 && order.payment_status !== 'paid' && (
                          <IconButton
                            size="small"
                            color="success"
                            title="Mark this order as paid"
                            onClick={() => handleMarkPaid(order)}
                          >
                            <PaidIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteOrder(order.order_id)}
                          color="error"
                          title="Permanently delete this order (no audit trail — prefer Void)"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No retail orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredOrders.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Order Dialog */}
      <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {editingOrder ? 'Edit Order' : 'Create Order'}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingOrder && typeof editingOrder.source_file === 'string' && editingOrder.source_file.startsWith('order-pdfs/') && (
              <Button size="small" variant="text" onClick={() => downloadOrderSourcePdf(selectedBrand.id, editingOrder.order_id)}>
                View Source PDF
              </Button>
            )}
            {!editingOrder && orderForm.source_file && (
              <Tooltip title="The uploaded PDF is stored and will be linked to this order when you save">
                <Chip size="small" color="success" variant="outlined" label="PDF attached" />
              </Tooltip>
            )}
            {!editingOrder && (
              <Button size="small" variant="outlined" onClick={() => setPdfOpen(true)}>
                Upload PDF
              </Button>
            )}
          </Box>
        </DialogTitle>
        <OrderPdfUpload
          open={pdfOpen}
          onClose={() => setPdfOpen(false)}
          onApply={applyPdfPrefill}
          orderType="retail"
          brandId={selectedBrand?.id}
        />
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Order Name"
                  value={orderForm.order_name}
                  onChange={(e) => setOrderForm({ ...orderForm, order_name: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Order Date"
                  type="date"
                  value={orderForm.event_date}
                  onChange={(e) => setOrderForm({ ...orderForm, event_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '2000-01-01' }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Source Warehouse</InputLabel>
                  <Select
                    value={orderForm.warehouse_code}
                    label="Source Warehouse"
                    onChange={(e) => handleWarehouseChange(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Auto (LEFO)</em>
                    </MenuItem>
                    {warehouses.map(w => (
                      <MenuItem key={w.code} value={w.code}>
                        {w.name} ({w.code})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {orderForm.warehouse_code
                      ? 'Recommended from this customer’s order history — change to override'
                      : 'No history yet — pick a warehouse to ship from'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Invoicing Subsidiary</InputLabel>
                  <Select
                    value={orderForm.subsidiary_id || 0}
                    label="Invoicing Subsidiary"
                    onChange={(e) => setOrderForm({ ...orderForm, subsidiary_id: Number(e.target.value) || 0 })}
                  >
                    <MenuItem value={0}>
                      <em>Unmapped</em>
                    </MenuItem>
                    {subsidiaries.map(s => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Legal entity that invoices this order — auto-inherited from the customer; set here to override
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 2 }}>Customer Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Customer Name"
                  value={orderForm.customer_name}
                  onChange={(e) => setOrderForm({ ...orderForm, customer_name: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Email"
                  type="email"
                  value={orderForm.customer_email}
                  onChange={(e) => setOrderForm({ ...orderForm, customer_email: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Phone"
                  value={orderForm.customer_phone}
                  onChange={(e) => setOrderForm({ ...orderForm, customer_phone: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 2 }}>Products</Typography>
            {orderForm.products_info.map((product, index) => (
              <Paper key={index} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    {/* Type-ahead SKU picker with thumbnail + product name. The previous
                        <Select> rendered 400+ MenuItems flat with no search — unusable
                        for brands with large catalogs (Rishabh's reported "no SKU appearing"). */}
                    <Autocomplete
                      size="small"
                      options={products}
                      getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.sku || ''))}
                      isOptionEqualToValue={(a, b) => (typeof a === 'string' ? a : a?.sku) === (typeof b === 'string' ? b : b?.sku)}
                      value={products.find(p => p.sku === product.product_sku) || null}
                      onChange={(e, v) => handleUpdateProduct(index, 'product_sku', (v && v.sku) || '')}
                      filterOptions={(opts, state) => {
                        const q = (state.inputValue || '').toLowerCase().trim();
                        if (!q) return opts.slice(0, 100);
                        return opts.filter(o =>
                          (o.sku || '').toLowerCase().includes(q) ||
                          (o.product_name || '').toLowerCase().includes(q) ||
                          (o.gtin || '').toLowerCase().includes(q)
                        ).slice(0, 100);
                      }}
                      noOptionsText={products.length === 0 ? 'No products for this brand — set up product_cogs first' : 'No SKU matches'}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id || option.sku} sx={{ display: 'flex', gap: 1, py: 0.5 }}>
                          {option.image_url ? (
                            <Box component="img" src={option.image_url}
                              sx={{ width: 36, height: 36, borderRadius: 0.5, objectFit: 'cover', border: '1px solid #ddd', bgcolor: 'common.white' }}
                              onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                          ) : (
                            <Box sx={{ width: 36, height: 36, borderRadius: 0.5, bgcolor: 'grey.200' }} />
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>{option.sku}</Typography>
                            <Typography variant="caption" color="textSecondary" noWrap>
                              {option.product_name || '(no name)'}
                            </Typography>
                          </Box>
                          {(option.calculated_cogs || option.cogs) && (
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                              {Number(option.calculated_cogs || option.cogs).toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="SKU" required
                          placeholder={products.length ? `${products.length} SKUs · type to search` : 'No products available'} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Qty"
                      type="number"
                      value={product.quantity}
                      onChange={(e) => handleUpdateProduct(index, 'quantity', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Unit Price"
                      type="number"
                      value={product.unit_price}
                      onChange={(e) => handleUpdateProduct(index, 'unit_price', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={11} sm={4}>
                    <FormControl fullWidth size="small" disabled={!orderForm.warehouse_code || !product.product_sku}>
                      <InputLabel>Batch (Optional)</InputLabel>
                      <Select
                        value={product.batch_id || ''}
                        label="Batch (Optional)"
                        onChange={(e) => handleUpdateProduct(index, 'batch_id', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Auto (LEFO - freshest)</em>
                        </MenuItem>
                        {(availableBatches[product.product_sku] || []).map(batch => (
                          <MenuItem key={batch.id} value={batch.id}>
                            {batch.batch_number || `#${batch.id}`} | {batch.current_quantity} avail
                            {batch.has_expiry && batch.expiry_date ? ` | Exp: ${batch.expiry_date.split('T')[0]}` : ''}
                            {batch.unit_cost ? ` | ${getCurrencySymbol(orderForm.currency)}${parseFloat(batch.unit_cost).toFixed(2)}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveProduct(index)}
                      disabled={orderForm.products_info.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Product
            </Button>

            <Typography variant="h6" sx={{ mt: 2 }}>Payment & Order Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={orderForm.currency}
                    label="Currency"
                    onChange={(e) => setOrderForm({ ...orderForm, currency: e.target.value })}
                  >
                    <MenuItem value="GBP">GBP (£)</MenuItem>
                    <MenuItem value="USD">USD ($)</MenuItem>
                    <MenuItem value="EUR">EUR (€)</MenuItem>
                    <MenuItem value="AUD">AUD (A$)</MenuItem>
                    <MenuItem value="CAD">CAD (C$)</MenuItem>
                    <MenuItem value="INR">INR (₹)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <InputLabel>Payment Status</InputLabel>
                  <Select
                    value={orderForm.payment_status}
                    label="Payment Status"
                    onChange={(e) => setOrderForm({ ...orderForm, payment_status: e.target.value })}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="partial">Partial</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Payment Method"
                  value={orderForm.payment_method}
                  onChange={(e) => setOrderForm({ ...orderForm, payment_method: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Payment Terms"
                  value={orderForm.payment_terms}
                  onChange={(e) => setOrderForm({ ...orderForm, payment_terms: e.target.value })}
                  placeholder="e.g., Net 30"
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="PO Number"
                  value={orderForm.po_number}
                  onChange={(e) => setOrderForm({ ...orderForm, po_number: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Invoice Number"
                  value={orderForm.invoice_number}
                  onChange={(e) => setOrderForm({ ...orderForm, invoice_number: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Sales Rep"
                  value={orderForm.sales_rep}
                  onChange={(e) => setOrderForm({ ...orderForm, sales_rep: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <TextField
              label="Notes"
              value={orderForm.notes}
              onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="h6" color="primary.contrastText">
                Total: {getCurrencySymbol(orderForm.currency)}{calculateTotal().toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveOrder} variant="contained">
            {editingOrder ? 'Update' : 'Create'} Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Retail Orders CSV</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>Required columns:</Typography>
              <Typography variant="body2">
                • order_name, event_date (YYYY-MM-DD), customer_name, product_sku, quantity, unit_price
              </Typography>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Optional columns:</Typography>
              <Typography variant="body2">
                • currency (GBP, USD, EUR, etc.), payment_status, payment_terms, po_number, invoice_number, sales_rep
              </Typography>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Multi-product format:</Typography>
              <Typography variant="body2">
                • Use "products" column with format: SKU1:qty:price;SKU2:qty:price
              </Typography>
            </Alert>
            <TextField
              type="file"
              inputProps={{ accept: '.csv' }}
              onChange={(e) => setCsvFile(e.target.files[0])}
              fullWidth
              helperText="Maximum 10,000 rows per upload"
            />
            {csvFile && (
              <Typography variant="body2" color="primary">
                Selected: {csvFile.name} ({(csvFile.size / 1024).toFixed(2)} KB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCsvUpload} 
            variant="contained" 
            disabled={!csvFile || loading}
          >
            {loading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void / cancel-shipment dialog — required reason → audit log row.
          For pending: marks the order voided (is_voided=1) and releases the
          hold. For shipped / partially_shipped: cancels the latest shipment,
          restores inventory to the original batches via hard-delete of the
          shipment-caused inventory_transactions rows, sends the order back
          to pending. Repeat to revert older partial shipments; void from
          pending when ready to fully cancel. */}
      <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {['shipped', 'partially_shipped'].includes(voidTarget?.fulfillment_status)
            ? `Cancel shipment of ${(voidTarget?.order_id || '').replace(/^CIN7-/, '')}`
            : `Void ${(voidTarget?.order_id || '').replace(/^CIN7-/, '')}`}
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {voidTarget && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {['shipped', 'partially_shipped'].includes(voidTarget.fulfillment_status)
                  ? 'This will cancel the latest shipment and restore inventory to the original batches. The order itself stays alive — it returns to "pending" so it can be re-shipped, edited, or voided from there. A reason is required for the audit trail.'
                  : 'Voiding releases the inventory hold and marks the order cancelled. The row stays in history with a "voided" tag — it can\'t be un-voided. A reason is required for the audit trail.'}
              </Alert>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {voidTarget.customer_name || voidTarget.customer_id} — {voidTarget.currency} {Number(voidTarget.total_amount ?? voidTarget.order_revenue).toFixed(2)}
              </Typography>
              <TextField
                fullWidth multiline rows={3} autoFocus
                label="Reason *"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="e.g. 'Customer cancelled', 'Created in error', 'Duplicate of ...'"
                helperText="Recorded to the audit log against your user account"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidOpen(false)} disabled={voiding}>Cancel</Button>
          <Button onClick={submitVoid} variant="contained" color="error" startIcon={<VoidIcon />} disabled={voiding || !voidReason.trim()}>
            {voiding
              ? (['shipped', 'partially_shipped'].includes(voidTarget?.fulfillment_status) ? 'Cancelling shipment…' : 'Voiding…')
              : (['shipped', 'partially_shipped'].includes(voidTarget?.fulfillment_status) ? 'Cancel shipment' : 'Confirm Void')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore (un-void) dialog */}
      <Dialog open={!!restoreTarget} onClose={() => !restoring && setRestoreTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Restore {restoreTarget?.order_name || restoreTarget?.order_id}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Restoring puts the order back to Pending and re-reserves its stock. If that stock has been used since the void, the restore fails and says which SKU is short. A reason is required for the audit trail.
          </Alert>
          <TextField fullWidth multiline rows={2} autoFocus label="Reason *"
            value={restoreReason} onChange={(e) => setRestoreReason(e.target.value)}
            placeholder="e.g. Voided by mistake" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTarget(null)} disabled={restoring}>Cancel</Button>
          <Button onClick={submitRestore} variant="contained" disabled={restoring || !restoreReason.trim()}>
            {restoring ? 'Restoring…' : 'Restore order'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}

export default RetailOrders;
